import JSZip from 'jszip'
import { Currencies, Money } from 'ts-money'
import { STATUS_PAYMENT_CATALOGUE } from '../../constants'
import { IPaginatedData, WithId } from 'models/shared.models'
import { UnparsedClient } from 'models/client'
import {
  CreditNote,
  CreditNoteFiles,
  IInvoiceDraft,
  IInvoice,
  InvoiceFiles,
  InvoiceForm,
  PaymentComplement,
  PaymentComplementFiles,
  TCFDIVersion,
  ICFDIsZIPUploadDetail,
  ICFDIsZIPUploadInfo,
} from 'models/invoices.models'
import RazonSocial from 'models/razonSocial'
import {
  compareSortByMultipleKeys,
  fileToBase64,
} from 'lib/helpers/shared.helpers'
import HTTPClient, { httpErrorParser } from 'lib/helpers/HTTPClient'
import { getDateInISO } from 'lib/helpers/utilities'
import { ICFDISyncInformation } from 'store/SharedData/sharedData.models'
import {
  IPaginatedDataResponse,
  IPaginatedQueryRequest,
} from 'services/Shared/shared.models'
import {
  ICfdiDraftResponse,
  IDeleteInvoiceDraftRequest,
  IInvoiceResponse,
  ICFDIsZIPUploadDetailResponse,
  ICFDIsZIPUploadDetailsFiltersRequest,
  ICFDIsZIPUploadInfoRequest,
  IUploadXMLCFDIRequest,
  TCFDIsZIPUploadInfoResponse,
} from './invoices.models'
import { createInvoiceCreationRequestBody } from './invoices.utils'
import { parseClient } from 'lib/helpers/utilities'

const route = process.env.REACT_APP_APIURL || '/api/'

export const getSyncInformation = async (
  activeWorkspace: number
): Promise<ICFDISyncInformation[]> => {
  const client = HTTPClient.getClient()
  try {
    const url = `${route}/workspaces/${activeWorkspace}/requestDetail`

    const response = await client.get(url)
    const data: ICFDISyncInformation[] = await response.json()
    const formattedData: ICFDISyncInformation[] = data.map(detail => ({
      ...detail,
      id: detail.idRequest,
    }))
    return formattedData
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getCfdiList = async ({
  activeWorkspace,
  page,
  pageSize,
  filters,
}: IPaginatedQueryRequest<{
  rfc?: string
  search?: string
  advance?: boolean
  type?: string
  status?: number
  startDate?: string
  endDate?: string
  receptor?: string
}>): Promise<IPaginatedData<WithId<IInvoice>>> => {
  const client = HTTPClient.getClient()
  try {
    let url = `${route}/workspaces/${activeWorkspace}/cfdis?page=${page + 1}&size=${pageSize}&`

    if (filters) {
      const {
        rfc,
        search,
        advance,
        type,
        status,
        startDate,
        endDate,
        receptor,
      } = filters

      if (rfc) url = `${url}rfc=${rfc}&`
      if (search) url = `${url}search=${search}&`
      if (advance) url = `${url}advanced_search=true&`
      if (type) url = `${url}type=${type}&`
      if (status) url = `${url}status=${status}&`
      if (startDate) url = `${url}fecha_inicio=${startDate}&`
      if (endDate) url = `${url}fecha_final=${endDate}&`
      if (receptor) url = `${url}receptor=${receptor}&`
    }

    url = url.substring(0, url.length - 1)

    const petition = await client.get(url)

    const data = await petition.json()
    const cfdis: IInvoiceResponse[] = data.cfdis

    const parsedList: WithId<IInvoice>[] = cfdis.map(
      (d, i): WithId<IInvoice> => {
        const subtotal = d['cfdiTaxes']
          ? Number(d['cfdiTaxes']['subtotal'] ?? 0)
          : 0
        const total = d['cfdiTaxes'] ? Number(d['cfdiTaxes']['total'] ?? 0) : 0

        return {
          ...d['cfdiInformation'],
          id: d.cfdiInformation.idCfdi ?? d.cfdiInformation.uuid ?? i,
          createdAt: d['cfdiCreated'],
          hour: d['cfdiCreated'],
          statusPayment: STATUS_PAYMENT_CATALOGUE.find(
            c => c.value === d['cfdiInformation']['statusPayment']
          ),
          emisor: d['cdfiEmisor'] as RazonSocial,
          subtotal,
          taxes: total - subtotal,
          total,
          imported: d['cfdiInformation']['imported'] || false,
        }
      }
    )

    const list = parsedList.sort((a, b) =>
      compareSortByMultipleKeys(a, b, ['createdAt'], ['desc'])
    )

    return {
      list,
      total: data.totalElements,
    }
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getCFDIDrafts = async ({
  activeWorkspace,
  page,
  pageSize,
  filters,
}: IPaginatedQueryRequest<{
  rfc?: string
  search?: string
  advance?: boolean
  type?: string
  status?: number
  startDate?: string
  endDate?: string
  receptor?: string
}>): Promise<IPaginatedData<WithId<IInvoiceDraft>>> => {
  const client = HTTPClient.getClient()
  try {
    let url = `${route}/workspaces/${activeWorkspace}/invoiceDrafts?page=${page + 1}&size=${pageSize}&`

    if (filters) {
      const {
        rfc,
        search,
        advance,
        type,
        status,
        startDate,
        endDate,
        receptor,
      } = filters

      if (rfc) url = `${url}rfc=${rfc}&`
      if (search) url = `${url}search=${search}&`
      if (advance) url = `${url}advanced_search=true&`
      if (type) url = `${url}type=${type}&`
      if (status) url = `${url}status=${status}&`
      if (startDate) url = `${url}fecha_inicio=${startDate}&`
      if (endDate) url = `${url}fecha_final=${endDate}&`
      if (receptor) url = `${url}receptor=${receptor}&`
    }

    url = url.substring(0, url.length - 1)

    const petition = await client.get(url)

    const data: IPaginatedDataResponse<ICfdiDraftResponse> =
      await petition.json()

    const listWithId: WithId<IInvoiceDraft>[] = data.record.map(
      (draft: ICfdiDraftResponse, i) => ({
        ...draft,
        conceptos: draft.conceptos.map(c => ({
          ...c,
          price: c.price === '0' ? '' : c.price,
          cantidad: c.cantidad === '0' ? '' : c.cantidad,
        })),
        clients: draft.clients
          ? (parseClient(draft.clients) as unknown as UnparsedClient)
          : undefined,
        emisor: draft.emisor as RazonSocial,
        exchangeRate: draft.exchangeRate === '0.0' ? '' : draft.exchangeRate,
        id: draft.idRequest,
        statusPayment: STATUS_PAYMENT_CATALOGUE.find(
          c => c.value === draft.statusPayment
        ),
      })
    )

    return {
      list: listWithId,
      total: data.totalElements,
    }
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const deleteInvoiceDraft = async ({
  activeWorkspace,
  draftId,
}: IDeleteInvoiceDraftRequest): Promise<void> => {
  const client = HTTPClient.getClient()
  try {
    return await client.delete(
      `${route}/workspaces/${activeWorkspace}/${draftId}`
    )
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getCfdiListFilesInZip = async (
  workspaceId: number,
  page: number,
  size: number,
  rfc?: string,
  search?: string,
  advance?: boolean,
  type?: string,
  status?: number,
  startDate?: string,
  endDate?: string,
  receptor?: string
): Promise<string> => {
  const client = HTTPClient.getClient()
  try {
    let url = `${route}/workspaces/${workspaceId}/cfdis/download/zip?page=${page + 1}&size=${size}&`
    if (rfc) url = `${url}rfc=${rfc}&`
    if (search) url = `${url}search=${search}&`
    if (advance) url = `${url}advanced_search=true&`
    if (type) url = `${url}type=${type}&`
    if (status) url = `${url}status=${status}&`
    if (startDate) url = `${url}fecha_inicio=${startDate}&`
    if (endDate) url = `${url}fecha_final=${endDate}&`
    if (receptor) url = `${url}receptor=${receptor}&`
    url = url.substring(0, url.length - 1)

    const petition = await client.get(url)
    const data: string = await petition.text()
    return data
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getCfdiById = async (
  workspaceId: number,
  invoiceId: number
): Promise<IInvoice> => {
  const client = HTTPClient.getClient()
  try {
    const petition = await client.get(
      `${route}/workspaces/${workspaceId}/cfdis/${invoiceId}`
    )
    const data = await petition.json()
    const subtotal = Money.fromDecimal(
      Number(data['cfdiTaxes']['subtotal']),
      Currencies.MXN
    )
    const total = Money.fromDecimal(
      Number(data['cfdiTaxes']['total']),
      Currencies.MXN
    )

    const invoice: IInvoice = {
      ...data['cfdiInformation'],
      conceptos: data['cfdiConcepts'],
      emisor: data['cfdiEmisor'],
      subtotal: subtotal.toDecimal(),
      taxes: total.subtract(subtotal).toDecimal(),
      total: total.toDecimal(),
      statusPayment: STATUS_PAYMENT_CATALOGUE.find(
        c => c.value === data['statusPayment']
      ),
      imported: data['imported'] || false,
    }

    return invoice
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getCfdiFilesService = async (
  workspaceId: number,
  invoice: IInvoice
): Promise<InvoiceFiles> => {
  const client = HTTPClient.getClient()
  try {
    const [dataPdf, dataXml] = await Promise.all([
      client
        .get(
          `${route}/workspaces/${workspaceId}/cfdis/${invoice.idCfdi ?? invoice.uuid}/download/pdf`
        )
        .then((res: any) => res.text()),
      client
        .get(
          `${route}/workspaces/${workspaceId}/cfdis/${invoice.idCfdi ?? invoice.uuid}/download/xmlCfdi`
        )
        .then((res: any) => res.text()),
    ])

    return {
      pdf: `data:application/pdf;base64,${dataPdf}`,
      xml: `data:application/xml;base64,${dataXml}`,
      invoice,
    }
  } catch (err: any) {
    throw await httpErrorParser(err)
  }
}

export const getCfdisFiles = async (
  workspaceId: number,
  list: IInvoice[]
): Promise<string> => {
  const invoiceList = list.filter(invoice => !!(invoice.uuid || invoice.idCfdi))

  try {
    if (!invoiceList.length) {
      throw new Error('No se encontraron los ids de las facturas.')
    }

    const files = await Promise.all(
      invoiceList.map(invoice => getCfdiFilesService(workspaceId, invoice))
    )
    const zip = new JSZip()
    files.forEach(({ pdf, xml, invoice }) => {
      const uuid = invoice?.uuid ?? 'XXXX0000-0000-XXXX-0000-XXXX0000XXXX'
      zip.file(`${uuid}.pdf`, pdf.split(',')[1], { base64: true })
      zip.file(`${uuid}.xml`, xml.split(',')[1], { base64: true })
    })
    return zip.generateAsync({ type: 'base64' })
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getCfdiCancelNotice = async (
  workspaceId: number,
  invoice: IInvoice
): Promise<string> => {
  const client = HTTPClient.getClient()
  try {
    const petition = await client.get(
      `${route}/workspaces/${workspaceId}/cancellation/acuse?rfcEmisor=${invoice.emisor.rfc}&uuid=${invoice.uuid}`
    )
    const data = await petition.json()
    return `data:application/pdf;base64,${data.pdf}`
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const sendCfdiByEmail = async (
  workspaceId: number,
  invoiceId: number
): Promise<void> => {
  const client = HTTPClient.getClient()
  try {
    const petition = await client.get(
      `${route}/workspaces/${workspaceId}/cfdis/${invoiceId}/send`
    )
    await petition.text()
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const changeCfdiPaymentStatus = async (
  workspaceId: number,
  invoice: IInvoice,
  status: string
): Promise<void> => {
  const client = HTTPClient.getClient()
  try {
    const petition = await client.put(
      `${route}/workspaces/${workspaceId}/cfdis/${invoice.uuid}/payment`,
      {
        statusPayment: status,
      }
    )
    await petition.text()
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getCfdiAmountPaid = async (
  workspaceId: number,
  invoice: IInvoice
): Promise<Money> => {
  const client = HTTPClient.getClient()
  try {
    const petition = await client.get(
      `${route}/workspaces/${workspaceId}/cfdis/${invoice.idCfdi}/amountpaid`
    )
    const data = await petition.json()
    const amount = Money.fromDecimal(Number(data['amountPaid']), Currencies.MXN)
    return amount
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const cancelCfdi = async (
  workspaceId: number,
  invoice: IInvoice,
  reason: number,
  substitute?: string
) => {
  const client = HTTPClient.getClient()
  try {
    let url = `${route}/workspaces/${workspaceId}/cancellation/request`
    if (invoice?.version === '4') url = `${url}40`
    const petition = await client.post(url, {
      uuid: invoice.uuid,
      idCancellation: reason,
      replacementFolio: substitute,
    })
    await petition.json()
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getPaymentComplementList = async (
  workspaceId: number,
  invoiceId: number
): Promise<PaymentComplement[]> => {
  const client = HTTPClient.getClient()
  try {
    const petition = await client.get(
      `${route}/workspaces/${workspaceId}/cfdis/${invoiceId}/Payment`
    )
    const data: PaymentComplement[] = await petition.json()
    return data
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getPaymentComplementFiles = async (
  workspaceId: number,
  invoiceId: number,
  paymentComplementId: number
): Promise<PaymentComplementFiles> => {
  const client = HTTPClient.getClient()
  try {
    const petitions = await Promise.all([
      client.get(
        `${route}/workspaces/${workspaceId}/cfdis/${invoiceId}/Payment/${paymentComplementId}/pdfDownload`
      ),
      client.get(
        `${route}/workspaces/${workspaceId}/cfdis/${invoiceId}/Payment/${paymentComplementId}/xmlDownload`
      ),
    ])
    const dataPdf = await petitions[0].text()
    const dataXml = await petitions[1].text()

    const pdfBase64 = `data:application/pdf;base64,${dataPdf}`
    const xmlBase64 = `data:application/xml;base64,${dataXml}`

    return {
      pdf: pdfBase64,
      xml: xmlBase64,
    }
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const saveInvoiceCreationProgress = async (
  activeWorkspace: number,
  form: InvoiceForm
): Promise<void> => {
  const client = HTTPClient.getClient()
  try {
    const body = createInvoiceCreationRequestBody(form, true)

    return await client.post(
      `${route}/workspaces/${activeWorkspace}/invoiceDraft`,
      body
    )
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const createPaymentComplement = async (
  workspaceId: number,
  invoice: IInvoice,
  form: any,
  version: TCFDIVersion
): Promise<void> => {
  const client = HTTPClient.getClient()
  try {
    const body = {
      paymentDate: getDateInISO(form.fechaPago),
      idWayOfPayment: form.formaPago?.idCatalogValues,
      operationNumber: form.numeroOperacion,
      idCurrency: form.moneda?.idCatalogValues,
      exchangeRate: form.tipoCambio,
      amount: Money.fromDecimal(form.monto, Currencies.MXN).toDecimal(),
      impuesto: !!form.tasa ? '2' : undefined,
      rate: !!form.tasa ? form.tasa : undefined,
      additionalInformation: [
        {
          rfcOrigen: form.rfcOrigen,
          rfcReceptor: form.rfcBeneficiario,
          bank: form.nombreBanco,
          beneficiaryAccount: form.cuentaBeneficiario,
          orderingAccount: form.cuentaOrdenante,
          typePaymentChain: form.tipoCadenaPago?.idCatalogValues || 0,
          paymentChain: form.cadenaPago,
          paymentCertificate: form.certificadoPago,
          paymentStamp: form.selloPago,
        },
      ],
      relatedDocuments: [
        {
          idRelatedDocument: invoice.uuid,
          previousBalance: form.importeSaldoAnterior,
          amountPaid: form.importePagado,
          insolteAmount: Money.fromDecimal(
            form.importeSaldoAnterior,
            Currencies.MXN
          )
            .subtract(Money.fromDecimal(form.importePagado, Currencies.MXN))
            .toDecimal(),
          partialityNumber: Number(form.numeroParcialidad),
        },
      ],
    }

    let url = `${route}/workspaces/${workspaceId}/cfdis/${invoice.idCfdi}/Payment`
    if (version === '4.0') url = `${url}/v4`

    const petition = await client.post(url, body)
    await petition.json()
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const sendPaymentComplementByEmail = async (
  workspaceId: number,
  invoiceId: number,
  paymentComplementId: number
): Promise<void> => {
  const client = HTTPClient.getClient()
  try {
    const petition = await client.get(
      `${route}/workspaces/${workspaceId}/cfdis/${invoiceId}/Payment/${paymentComplementId}/send`
    )
    await petition.data
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getCreditNoteList = async (
  workspaceId: number,
  invoiceId: number
): Promise<CreditNote[]> => {
  const client = HTTPClient.getClient()
  try {
    const petition = await client.get(
      `${route}/workspaces/${workspaceId}/cfdis/${invoiceId}/Creditnotes`
    )
    const data: CreditNote[] = await petition.json()
    return data
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getCreditNoteFiles = async (
  workspaceId: number,
  invoiceId: number,
  creditNoteId: number
): Promise<CreditNoteFiles> => {
  const client = HTTPClient.getClient()
  try {
    const petitions = await Promise.all([
      client.get(
        `${route}/workspaces/${workspaceId}/cfdis/${invoiceId}/creditNote/${creditNoteId}/pdfDownload`
      ),
      client.get(
        `${route}/workspaces/${workspaceId}/cfdis/${invoiceId}/creditNote/${creditNoteId}/xmlDownload`
      ),
    ])
    const dataPdf = await petitions[0].text()
    const dataXml = await petitions[1].text()

    const pdfBase64 = `data:application/pdf;base64,${dataPdf}`
    const xmlBase64 = `data:application/xml;base64,${dataXml}`

    return {
      pdf: pdfBase64,
      xml: xmlBase64,
    }
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const sendCreditNoteByEmail = async (
  workspaceId: number,
  invoiceId: number,
  creditNoteId: number
) => {
  const client = HTTPClient.getClient()
  try {
    const petition = await client.get(
      `${route}/workspaces/${workspaceId}/cfdis/${invoiceId}/creditNote/${creditNoteId}/send`
    )
    await petition.data
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const uploadXMLFileService = async (
  params: IUploadXMLCFDIRequest
): Promise<void> => {
  const { workspaceId, file } = params
  const client = HTTPClient.getClient()

  try {
    const fileAsBase64: string = await fileToBase64(file)
    await client.post(`${route}/workspaces/${workspaceId}/cfdis/fileUpload`, {
      file: fileAsBase64,
      name: file.name,
    })
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getCFDIsZIPUploadInfo = async ({
  workspaceId,
  jobId,
}: ICFDIsZIPUploadInfoRequest): Promise<ICFDIsZIPUploadInfo> => {
  const client = HTTPClient.getClient()

  try {
    const petition = await client.get(
      `${route}/workspaces/${workspaceId}/cfdis/fileUpload/${jobId}`
    )
    const { name, extension, totalFiles }: TCFDIsZIPUploadInfoResponse =
      await petition.json()

    const formattedData: ICFDIsZIPUploadInfo = {
      zipFileName: name,
      zipFileExtension: extension,
      totalFiles,
    }
    return formattedData
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const getCFDIsZIPUploadDetails = async ({
  activeWorkspace,
  page,
  pageSize,
  filters,
}: IPaginatedQueryRequest<ICFDIsZIPUploadDetailsFiltersRequest>): Promise<
  IPaginatedData<ICFDIsZIPUploadDetail>
> => {
  const client = HTTPClient.getClient()

  try {
    const { category, jobId } = filters ?? {}

    const petition = await client.get(
      `${route}/workspaces/${activeWorkspace}/cfdis/fileUpload/${jobId}/children?numberPage=${page}&pageSize=${pageSize}&categoryMessage=${category}`
    )

    const data: IPaginatedDataResponse<ICFDIsZIPUploadDetailResponse> =
      await petition.json()

    const formattedData: IPaginatedData<ICFDIsZIPUploadDetail> = {
      total: data.totalElements,
      list: data.record.map(item => {
        const { idCfdiUploadFile, ...rest } = item

        return {
          ...rest,
          id: idCfdiUploadFile,
        }
      }),
    }

    return formattedData
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}

export const uploadZipFileService = async (
  params: IUploadXMLCFDIRequest
): Promise<void> => {
  const { workspaceId, file } = params
  const client = HTTPClient.getClient()

  try {
    const fileAsBase64: string = await fileToBase64(file)
    await client.post(`${route}/workspaces/${workspaceId}/cfdis/fileUpload`, {
      file: fileAsBase64,
      name: file.name,
    })
  } catch (err: any) {
    const error = await httpErrorParser(err)
    throw error
  }
}
